import { monitorNREUM } from './utils/monitorNREUM'
import { createUniqueKey } from './utils/createUniqueKey'
import { BrowserConfiguration, Instance } from './types'
import { BrowserMethodsInterface } from './services/types'
import { BrowserMethods } from './services/methods'
import { setupConfig } from './config/setup'

/**
 * Classe responsável por inicializar a configuração do browser.
 */
export class BrowserConfig {
  /**
   * Configurações do browser.
   */
  config: BrowserConfiguration

  /**
   * Construtor da classe, recebe as configurações do browser.
   *
   * @param config - Configurações do browser.
   */
  constructor(config: BrowserConfiguration) {
    this.config = config
  }

  /**
   * Obtém o nome da aplicação e o nome único da variável.
   *
   * @returns {{ appName: string, varName: string }} - Nome da aplicação e nome único da variável.
   * @private
   */
  private getConfigNames(): { appName: string; varName: string } {
    const { appName } = this.config
    const varName = createUniqueKey(appName)
    return { appName, varName }
  }

  /**
   * Inicializa a instância do browser com as configurações especificadas.
   * @private
   */
  private initInstance(callback: (shouldIExecute: boolean) => void) {
    const { varName } = this.getConfigNames()
    const config = { ...this.config, varName }

    setupConfig(varName, config)

    monitorNREUM(varName)

    const shouldIExecute = window.nr[varName] !== undefined
    callback(shouldIExecute)
  }

  /**
   * Método público para inicialização da classe.
   *
   * @param instance - Função responsável por inicializar a instância do browser.
   */
  public init(instance: Instance) {
    const { varName } = this.getConfigNames()

    this.initInstance(shouldIExecute => {
      instance(varName, shouldIExecute)
    })
  }

  /**
   * Método que retorna as funções disponíveis na instância do browser.
   * @returns {BrowserMethodsInterface} Retorna uma instância de BrowserMethods, que contém as funções disponíveis na instância do browser.
   */
  public get methods(): BrowserMethodsInterface {
    const { varName } = this.getConfigNames()

    return BrowserMethods(varName)
  }
}

export type { BrowserConfiguration }
